// src/Tools/policyTools.ts

import { FunctionDeclaration, Type } from "@google/genai";

//
// ───────────────────────────────────────────────
//  1) CALCULATE PREMIUM (CALL BACKEND)
// ───────────────────────────────────────────────
//

export const calculatePremiumDeclaration: FunctionDeclaration = {
  name: "calculate_premium",
  description:
    "Calculates motor insurance premiums based on vehicle information. **Invocation Condition**: Call this tool ONLY after you have collected the vehicle's manufacture year, estimated market value, and whether the car is electric or gasoline from the user. You MUST say 'ثانية واحدة أراجع النظام… One moment while I check the system.' immediately before calling this tool. Do NOT ask for confirmation to calculate. Do NOT call this if the user has not provided the value, year, and electric/gasoline status.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      manufactorYear: {
        type: Type.NUMBER,
        description:
          "Vehicle manufacture year as a NUMBER (e.g., 2025, 2018). Mandatory.",
      },
      vehiclevalue: {
        type: Type.NUMBER,
        description:
          "Estimated market price of the vehicle in EGP as a NUMBER (e.g., 2000000). Mandatory.",
      },
      isElectricCar: {
        type: Type.BOOLEAN,
        description:
          "Whether the vehicle is electric (true) or gasoline/petrol (false). Set to true if user says 'كهرباء' or 'electric', false if user says 'بنزين' or 'gasoline' or 'petrol'. Mandatory.",
      },
    },
    required: ["manufactorYear", "vehiclevalue", "isElectricCar"],
  },
};

//
// ───────────────────────────────────────────────
//  2) SHOW UI PACKAGES (BUTTONS)
// ───────────────────────────────────────────────
//

export const showPackagesUIDeclaration: FunctionDeclaration = {
  name: "show_packages_ui",
  description:
    "Displays a list of motor insurance companies with their packages in the UI. **Invocation Condition**: Call this when explicitly instructed by the system. The companies are automatically available from the previous calculation - you can pass an empty array [] for the companies parameter. Do NOT say 'One moment...' before calling this. After displaying, tell the user in Arabic: 'لو محتاج تستفسر عن اى باقة من الى ظاهرين قدامك انا اقدر اساعدك فيهم'. The user can select a package and must click the 'تأكيد الباقة' (Confirm Package) button to proceed. This tool triggers the 'system_ui user_clicked_package_confirm' event when the user clicks the confirm button. Users can ask questions about packages before confirming - use 'search_faq' to answer them.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      companies: {
        type: Type.ARRAY,
        description: "Array of companies with packages (optional - companies from calculate_premium are automatically used if empty array is passed).",
        items: {
          type: Type.OBJECT,
          properties: {
            companyNameAr: { type: Type.STRING },
            companyNameEn: { type: Type.STRING },
            companyPackages: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  packageCode: { type: Type.STRING },
                  packageTypeAr: { type: Type.STRING },
                  packageTypeEn: { type: Type.STRING },
                  totalpremium: { type: Type.NUMBER },
                },
                required: [
                  "packageCode",
                  "packageTypeAr",
                  "packageTypeEn",
                  "totalpremium",
                ],
              },
            },
          },
          required: [
            "companyNameAr",
            "companyNameEn",
            "companyPackages",
          ],
        },
      },
    },
    required: [],
  },
};

//
// ───────────────────────────────────────────────
//  3) SHOW POLICY CONFIRMATION UI (NO API CALL)
// ───────────────────────────────────────────────
//

export const showPolicyConfirmationUIDeclaration: FunctionDeclaration = {
  name: "show_policy_confirmation_ui",
  description:
    "Displays the final policy confirmation screen. **Invocation Condition**: Call this when explicitly instructed by the system (Policy Flow only, not Claim flow). Triggers 'system_ui user_clicked_policy_confirm'.",
  parameters: {
    type: Type.OBJECT,
    properties: {},
  },
};

//
// ───────────────────────────────────────────────
//  4) ASK USER TO UPLOAD NATIONAL ID FRONT
// ───────────────────────────────────────────────
//

export const requestNationalIdFrontDeclaration: FunctionDeclaration = {
  name: "request_national_id_front",
  description:
    "Displays the UI to upload the National ID Front. **Invocation Condition**: Call this when explicitly instructed by the system. Say 'محتاج صورة وش بطاقة الرقم القومي' then call this tool. Wait for 'system_ui user_uploaded_national_id_front'.",
  parameters: {
    type: Type.OBJECT,
    properties: {},
  },
};

//
// ───────────────────────────────────────────────
//  5) ASK USER TO UPLOAD NATIONAL ID BACK
// ───────────────────────────────────────────────
//

export const requestNationalIdBackDeclaration: FunctionDeclaration = {
  name: "request_national_id_back",
  description:
    "Displays the UI to upload the National ID Back. **Invocation Condition**: Call this when explicitly instructed by the system. Say 'ودلوقتي صورة ظهر البطاقة' then call this tool. Wait for 'system_ui user_uploaded_national_id_back'.",
  parameters: {
    type: Type.OBJECT,
    properties: {},
  },
};

//
// ───────────────────────────────────────────────
//  6) SAVE EXTRACTED NATIONAL ID DATA
// ───────────────────────────────────────────────
//

export const saveNationalIdDataDeclaration: FunctionDeclaration = {
  name: "save_national_id_data",
  description:
    "**DEPRECATED**. Do not use. Data extraction is handled automatically by the backend upon upload.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      national_id_number: { type: Type.STRING },
      profession: { type: Type.STRING },
      gender: { type: Type.STRING },
      religion: { type: Type.STRING },
      marital_status: { type: Type.STRING },
      expiration_date: { type: Type.STRING },
      issue_batch_date: { type: Type.STRING },
      full_name: { type: Type.STRING },
      address: { type: Type.STRING },
      birth_date: { type: Type.STRING },
      card_serial_number: { type: Type.STRING },
    },
    required: [],
  },
};

//
// ───────────────────────────────────────────────
//  7) ASK USER TO UPLOAD DRIVING LICENSE
// ───────────────────────────────────────────────
//

export const requestDrivingLicenseDeclaration: FunctionDeclaration = {
  name: "request_driving_license",
  description:
    "Displays the UI to upload the Driving License. **Invocation Condition**: Call this when explicitly instructed by the system. Say 'محتاج صورة رخصة القيادة' then call this tool. Wait for 'system_ui user_uploaded_driving_license'.",
  parameters: {
    type: Type.OBJECT,
    properties: {},
  },
};

//
// ───────────────────────────────────────────────
//  8) ASK USER TO UPLOAD VEHICLE LICENSE FRONT
// ───────────────────────────────────────────────
//

export const requestVehicleLicenseFrontDeclaration: FunctionDeclaration = {
  name: "request_vehicle_license_front",
  description:
    "Displays the UI to upload Vehicle License Front. **Invocation Condition**: Call this when explicitly instructed by the system. Say 'محتاج صورة وش رخصة العربية' then call this tool. Wait for 'system_ui user_uploaded_vehicle_license_front'.",
  parameters: {
    type: Type.OBJECT,
    properties: {},
  },
};

//
// ───────────────────────────────────────────────
//  9) ASK USER TO UPLOAD VEHICLE LICENSE BACK
// ───────────────────────────────────────────────
//

export const requestVehicleLicenseBackDeclaration: FunctionDeclaration = {
  name: "request_vehicle_license_back",
  description:
    "Displays the UI to upload Vehicle License Back. **Invocation Condition**: Call this when explicitly instructed by the system. Say 'ودلوقتي صورة ظهر رخصة العربية' then call this tool. Wait for 'system_ui user_uploaded_vehicle_license_back'.",
  parameters: {
    type: Type.OBJECT,
    properties: {},
  },
};

//
// ───────────────────────────────────────────────
// 10) SUBMIT POLICY REQUEST
// ───────────────────────────────────────────────
//

export const submitPolicyRequestDeclaration: FunctionDeclaration = {
  name: "submit_policy_request",
  description:
    "Submits the policy request to the backend. **Invocation Condition**: Call this when explicitly instructed by the system. You MUST say 'ثانية واحدة أراجع النظام… One moment while I check the system.' before calling. Returns a RequestId.",
  parameters: {
    type: Type.OBJECT,
    properties: {},
  },
};

//
// ───────────────────────────────────────────────
// 11) CONFIRM POLICY REQUEST (CALL BACKEND)
// ───────────────────────────────────────────────
//

export const confirmPolicyRequestDeclaration: FunctionDeclaration = {
  name: "confirm_policy_request",
  description:
    "Generates the Fawry payment link. **Invocation Condition**: Call this when explicitly instructed by the system. Say 'ثانية واحدة أراجع النظام…' before calling. Requires the RequestId from submit_policy_request.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      requestId: {
        type: Type.STRING,
        description: "The RequestId returned by submit_policy_request.",
      },
    },
    required: ["requestId"],
  },
};

//
// ───────────────────────────────────────────────
// 12) START CAMERA STREAM (VEHICLE SURVEY)
// ───────────────────────────────────────────────
//

export const startCameraStreamDeclaration: FunctionDeclaration = {
  name: "start_camera_stream",
  description:
    "Starts live camera for vehicle survey. **Invocation Condition**: Call this when starting the vehicle survey. Say 'افتح الكاميرا عشان المعاينة' then call this tool.",
  parameters: {
    type: Type.OBJECT,
    properties: {},
  },
};

//
// ───────────────────────────────────────────────
// 13) TAKE VEHICLE PICTURE (VEHICLE SURVEY)
// ───────────────────────────────────────────────
//

export const takeVehiclePictureDeclaration: FunctionDeclaration = {
  name: "take_vehicle_picture",
  description:
    "Captures a snapshot during survey. **Invocation Condition**: Call this when the user is positioned correctly at the requested side (Front/Right/Back/Left). Say 'ثبت الكاميرا… يتم التقاط الصورة' then call this tool.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      side: {
        type: Type.STRING,
        description: "Side to capture: 'Front', 'Right', 'Back', 'Left'.",
      },
    },
    required: ["side"],
  },
};

//
// ───────────────────────────────────────────────
// 14) REQUEST CLOSE CAMERA (VEHICLE SURVEY)
// ───────────────────────────────────────────────
//

export const requestCloseCameraDeclaration: FunctionDeclaration = {
  name: "request_close_camera",
  description:
    "Closes the camera. **Invocation Condition**: Call this after all 4 sides have been captured. Say 'المعاينة انتهت هقفل الكاميرا' then call this tool.",
  parameters: {
    type: Type.OBJECT,
    properties: {},
  },
};

//
// ───────────────────────────────────────────────
// 15) SHOW POLICY SURVEY OPTIONS (TWO BUTTONS)
// ───────────────────────────────────────────────
//

export const showPolicySurveyOptionsDeclaration: FunctionDeclaration = {
  name: "show_policy_survey_options",
  description:
    "Displays options: Live Survey vs Upload Images. **Invocation Condition**: Call this when explicitly instructed by the system. Ask 'تحب المعاينة لايف ولا رفع 4 صور للسيارة؟' then call this tool. Wait for 'system_ui user_selected_policy_live_survey' or 'system_ui user_selected_policy_upload_images'.",
  parameters: {
    type: Type.OBJECT,
    properties: {},
  },
};

//
// ───────────────────────────────────────────────
// 16) REQUEST POLICY VEHICLE IMAGES (UPLOAD 4 IMAGES)
// ───────────────────────────────────────────────
//

export const requestPolicyVehicleImagesDeclaration: FunctionDeclaration = {
  name: "request_policy_vehicle_images",
  description:
    "Displays UI to upload 4 vehicle images. **Invocation Condition**: Call this when explicitly instructed by the system. Say 'من فضلك ارفع 4 صور للسيارة' then call this tool. Wait for 'system_ui user_uploaded_policy_vehicle_images'.",
  parameters: {
    type: Type.OBJECT,
    properties: {},
  },
};

//
// ───────────────────────────────────────────────
// 17) ANALYZE POLICY VEHICLE IMAGES (POLICY FLOW ONLY)
// ───────────────────────────────────────────────
//

export const analyzePolicyVehicleImagesDeclaration: FunctionDeclaration = {
  name: "analyze_policy_vehicle_images",
  description:
    "Analyzes vehicle images for POLICY flow using Gemini AI to generate a detailed damage description in formal Egyptian Arabic. **Invocation Condition**: Call this when explicitly instructed by the system (Policy Flow only). You MUST say 'ثانية واحدة هحلل صور العربيه' immediately before calling this tool. This tool processes the 4 captured/uploaded images and returns a professional Arabic description of the vehicle's condition and damages. **CRITICAL**: After receiving the description response, read it naturally to the user in Egyptian Arabic. After you finish reading and audio playback completes, STOP. DO NOT call any other tools. Wait for system signal.",
  parameters: {
    type: Type.OBJECT,
    properties: {},
  },
};

//
// ───────────────────────────────────────────────
// 18) SUBMIT POLICY SURVEY REQUEST (VEHICLE SURVEY - POLICY FLOW ONLY)
// ───────────────────────────────────────────────
//

export const submitPolicySurveyRequestDeclaration: FunctionDeclaration = {
  name: "submit_policy_survey_request",
  description:
    "Submits the vehicle survey data for POLICY flow. **CRITICAL**: Call this function EXACTLY ONCE. **DO NOT** call it multiple times. **Invocation Condition**: Call this when explicitly instructed by the system (Policy Flow only, not Claim flow). Provide a description of damages found. Say 'هجمع البيانات وهبعتها للنظام…' before calling. After successful submission, wait for system instruction on what to do next.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      damage_description: {
        type: Type.STRING,
        description: "Description of vehicle condition and damages.",
      },
    },
    required: ["damage_description"],
  },
};

//
// ───────────────────────────────────────────────
//  19) SEARCH FAQ (VECTOR-BASED SEMANTIC SEARCH)
// ───────────────────────────────────────────────
//

export const searchFAQDeclaration: FunctionDeclaration = {
  name: "search_faq",
  description:
    "Searches the knowledge base. **Invocation Condition**: MANDATORY. Call this tool IMMEDIATELY for ANY user question before attempting to answer. **CRITICAL**: Do NOT say 'ثانية واحدة أراجع النظام' or 'ثانية وحدة اراجع النظام' before calling this tool. Call it DIRECTLY without any delay message. Do not answer from general knowledge first.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      question: {
        type: Type.STRING,
        description: "The user's question.",
      },
    },
    required: ["question"],
  },
};

//
// ───────────────────────────────────────────────
//  EXPORT ALL POLICY TOOLS AS ARRAY
// ───────────────────────────────────────────────
//

export const PolicyTools: FunctionDeclaration[] = [
  calculatePremiumDeclaration,
  showPackagesUIDeclaration,
  showPolicyConfirmationUIDeclaration,
  requestNationalIdFrontDeclaration,
  requestNationalIdBackDeclaration,
  saveNationalIdDataDeclaration,
  requestDrivingLicenseDeclaration,
  requestVehicleLicenseFrontDeclaration,
  requestVehicleLicenseBackDeclaration,
  submitPolicyRequestDeclaration,
  confirmPolicyRequestDeclaration,
  startCameraStreamDeclaration,
  takeVehiclePictureDeclaration,
  requestCloseCameraDeclaration,
  showPolicySurveyOptionsDeclaration,
  requestPolicyVehicleImagesDeclaration,
  analyzePolicyVehicleImagesDeclaration,
  submitPolicySurveyRequestDeclaration,
  searchFAQDeclaration,
];
